<app-page-bar></app-page-bar>

<app-page>
  <h2>Merge duplicates — {{ businessId }}</h2>

  <section>
    <div class="stale banner" *ngIf="staleMessage">{{ staleMessage }}</div>
    <div class="error banner" *ngIf="errorMessage">{{ errorMessage }}</div>

    <!-- N>2: pick which member is the duplicate to merge into the survivor. -->
    <div class="member-selector" *ngIf="otherMembers.length > 1 && duplicateId === undefined">
      <h3>Choose the duplicate to merge into survivor #{{ survivorId }}</h3>
      <mat-radio-group class="members" [ngModel]="duplicateId" (ngModelChange)="selectDuplicate($event)">
        <mat-radio-button *ngFor="let member of otherMembers" [value]="member.end_customer_id">
          #{{ member.end_customer_id }} {{ member.name || '(no name)' }}
          <span class="muted">— {{ member.invoice_count }} invoices</span>
        </mat-radio-button>
      </mat-radio-group>
    </div>

    <ng-container *ngIf="preview as p">
      <div class="pairing">
        Survivor <strong>#{{ p.destination_id }} {{ memberName(p.destination_id) }}</strong>
        &nbsp;·&nbsp; deleting <strong>#{{ p.source_id }} {{ memberName(p.source_id) }}</strong>
      </div>

      <!-- Three columns: survivor | duplicate | result -->
      <div class="merge-grid">
        <div class="grid-head field">Field</div>
        <div class="grid-head">Survivor #{{ p.destination_id }}</div>
        <div class="grid-head">Duplicate #{{ p.source_id }}</div>
        <div class="grid-head">Result</div>

        <!-- Business id is never picked — always the trimmed value. -->
        <div class="field">Business ID</div>
        <div class="value fixed">{{ businessId }}</div>
        <div class="value fixed">{{ businessId }}</div>
        <div class="result">{{ businessId }} <span class="muted">(fixed)</span></div>

        <ng-container *ngFor="let g of p.field_groups">
          <!-- Groups that agree: one plain value, no picker. -->
          <ng-container *ngIf="!g.differs">
            <div class="field">{{ groupLabel(g.group) }}</div>
            <div class="value agree">{{ displayValue(g.destination_value) }}</div>
            <div class="value agree">{{ displayValue(g.source_value) }}</div>
            <div class="result">{{ displayValue(resultValue(g)) }}</div>
          </ng-container>

          <!-- Groups that differ: pick the survivor or the duplicate.
               Keyboard-operable in place: Enter and Space do what a click does, and Space must not also scroll the page. -->
          <ng-container *ngIf="g.differs">
            <div class="field" [class.conflict]="isConflict(g)">{{ groupLabel(g.group) }}</div>
            <div class="value option"
                 role="radio"
                 [attr.aria-checked]="pickFor(g.group) === PickSide.Destination"
                 [class.picked]="pickFor(g.group) === PickSide.Destination"
                 [class.conflict]="isConflict(g)"
                 tabindex="0"
                 (click)="setPick(g.group, PickSide.Destination)"
                 (keydown.enter)="setPick(g.group, PickSide.Destination)"
                 (keydown.space)="$event.preventDefault(); setPick(g.group, PickSide.Destination)">
              <mat-icon>{{ pickFor(g.group) === PickSide.Destination ? 'radio_button_checked' : 'radio_button_unchecked' }}</mat-icon>
              <span>{{ displayValue(g.destination_value) }}</span>
            </div>
            <div class="value option"
                 role="radio"
                 [attr.aria-checked]="pickFor(g.group) === PickSide.Source"
                 [class.picked]="pickFor(g.group) === PickSide.Source"
                 [class.conflict]="isConflict(g)"
                 tabindex="0"
                 (click)="setPick(g.group, PickSide.Source)"
                 (keydown.enter)="setPick(g.group, PickSide.Source)"
                 (keydown.space)="$event.preventDefault(); setPick(g.group, PickSide.Source)">
              <mat-icon>{{ pickFor(g.group) === PickSide.Source ? 'radio_button_checked' : 'radio_button_unchecked' }}</mat-icon>
              <span>{{ displayValue(g.source_value) }}</span>
            </div>
            <div class="result">{{ displayValue(resultValue(g)) }}</div>
          </ng-container>
        </ng-container>
      </div>

      <!-- Related-data panel (8.3) -->
      <div class="related">
        <h3>Related data</h3>
        <table class="related-table">
          <thead>
            <tr>
              <th></th>
              <th>Survivor #{{ p.destination_id }}</th>
              <th>Duplicate #{{ p.source_id }}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Contacts</td>
              <td>{{ p.destination_related.contact_count }}</td>
              <td>{{ p.source_related.contact_count }}</td>
            </tr>
            <tr>
              <td>Invoices</td>
              <td>{{ p.destination_related.invoice_count }}</td>
              <td>{{ p.source_related.invoice_count }}</td>
            </tr>
            <tr>
              <td>Credit insurances (active)</td>
              <td>{{ p.destination_related.credit_insurance_count }} ({{ p.destination_related.active_credit_insurance_count }})</td>
              <td>{{ p.source_related.credit_insurance_count }} ({{ p.source_related.active_credit_insurance_count }})</td>
            </tr>
            <tr>
              <td>Comments</td>
              <td>{{ p.destination_related.comment_count }}</td>
              <td>{{ p.source_related.comment_count }}</td>
            </tr>
            <tr>
              <td>YTJ snapshots</td>
              <td>{{ p.destination_related.ytj_snapshot_count }}</td>
              <td>{{ p.source_related.ytj_snapshot_count }}</td>
            </tr>
          </tbody>
        </table>

        <p class="warn-line" *ngIf="p.duplicate_contact_pairs > 0">
          {{ p.duplicate_contact_pairs }} identical contact pair(s) will be moved, not merged — clean up manually if needed.
        </p>

        <div class="overlaps" *ngIf="p.discount_overlaps.length > 0">
          <h4>Client-discount overlaps</h4>
          <table class="related-table">
            <thead>
              <tr>
                <th>Client</th>
                <th>Survivor discount</th>
                <th>Duplicate discount</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let o of p.discount_overlaps">
                <td>#{{ o.client_id }}</td>
                <td>{{ o.destination_discount }} <span class="muted" *ngIf="o.destination_updated">({{ o.destination_updated | date }})</span></td>
                <td>{{ o.source_discount }} <span class="muted" *ngIf="o.source_updated">({{ o.source_updated | date }})</span></td>
              </tr>
            </tbody>
          </table>
        </div>

        <p class="muted" *ngIf="p.remaining_member_ids.length > 0">
          {{ p.remaining_member_ids.length }} more member(s) remain in this group after this merge.
        </p>
      </div>

      <div class="actions">
        <button mat-raised-button color="primary" (click)="onMerge()">Merge — deletes #{{ p.source_id }}</button>
      </div>
    </ng-container>

    <!-- Keep-separate is always available for the whole group. -->
    <div class="keep-separate">
      <h3>Not a duplicate?</h3>
      <mat-form-field class="note">
        <mat-label>Reason (optional)</mat-label>
        <input matInput [(ngModel)]="note" />
      </mat-form-field>
      <button mat-stroked-button (click)="onKeepSeparate()">Keep separate</button>
    </div>
  </section>
</app-page>
